import React from "react";
import { Label, type TypeLabelProps } from "@sproutsocial/seeds-react-label";
import { useMenuToggleContext } from "./MenuContext";

export interface TypeMenuLabelProps extends Omit<TypeLabelProps, "htmlFor"> {
  // The children and required types are redundant, but they power the MenuLabel PropTable.
  children: React.ReactNode;
  /** Whether making a selection in the associated menu is required. */
  required?: boolean;
  /** ID of the associated form element. This prop is never valid to pass into MenuLabel
   *  because MenuLabel passes its own htmlFor prop to the underlying Label component.*/
  htmlFor?: never;
}

/**
 * @link https://seeds.sproutsocial.com/components/seeds-react-menu/#menulabel
 *
 * @description MenuLabel extends Label to create an accessible label for a Menu's toggle element.
 * A MenuLabel will automatically be associated with the corresponding menuToggleElement,
 * so an `htmlFor` prop is not necessary or allowed.
 *
 * @see {@link https://seeds.sproutsocial.com/components/seeds-react-menu/#menutogglebutton MenuToggleButton}
 * @see {@link https://seeds.sproutsocial.com/components/label/ Label}
 */
export const MenuLabel = ({
  children,
  required = false,
  ...rest
}: TypeMenuLabelProps) => {
  const { getLabelProps } = useMenuToggleContext();
  return (
    // This fallback id should never be used, but it is here to satisfy TypeScript since getLabelProps could be undefined.
    <Label
      htmlFor="fallback_menu_label"
      required={required}
      {...getLabelProps?.()}
      {...rest}
    >
      {children}
    </Label>
  );
};
